import React, { ChangeEvent, useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Button, Card, CardActionArea, CardActions, CardContent, CardMedia, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { getDoc, doc, getFirestore, setDoc } from 'firebase/firestore';
import { uploadString, ref, getStorage } from "firebase/storage";
import { queryEmployeeFromStateOrDb } from "../../utils/queryEmployeeFromStateOrDb";
import { queryEmployeeDocuments } from "../../utils/queryEmployeeDocuments";
import { AttachFileRounded } from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { Employee } from "../../types/Employee";
import { useAppSelector } from "../../store";
import { PERMISSIONS_USERS } from "../../security/Permission";
import { SecureComponent } from "../../security/SecureComponent";
import { ROLES_PERMISSIONS_MAPPING } from "../../security/Role";
import { PassportRequestForm } from "../dashboard/passport/PassportRequestForm";
import { EmployeePageContext } from './EmployeePage';


interface UploadDocForm {
  documentType: string;
  documentExpire: DateTime;
  onTypeChange: (type: string) => void;
  onExpireChange: (expire: DateTime) => void;
  onDataChange: (data: string) => void;
};
const UploadDocForm = (props: UploadDocForm) => {
  const { documentType, documentExpire, onTypeChange, onExpireChange, onDataChange } = props;

  const handleDocTypeOnChange = (event: SelectChangeEvent) => {
    onTypeChange(event.target.value as string)
  };

  const handleDocExpireOnChange = (date: DateTime) => {
    onExpireChange(date)
  };

  const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(event.target.files[0]);
      fileReader.onload = (e) => {
        if (e.target && e.target.result && typeof e.target.result === 'string') {
          onDataChange(e.target.result)
        }
      };
    }
  };

  return (
    <Box>
      <Box mt={3} mb={1}>
        <FormControl fullWidth>
          <InputLabel id="doument-select-input">Document Type</InputLabel>
          <Select
            labelId="doument-select-input"
            id="id-doument-select-input"
            value={documentType}
            label="Document Type"
            onChange={handleDocTypeOnChange}
          >
            <MenuItem value="Iqama">Iqama</MenuItem>
            <MenuItem value="Passport">Passport</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box mt={3} mb={1}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DatePicker
            label="Expire Date"
            format="yyyy/MM/dd"
            value={documentExpire}
            onChange={(newValue) => {
              if (newValue)
                handleDocExpireOnChange(newValue);
            }}
          />
        </LocalizationProvider>
      </Box>
      <Button variant="contained" component="label" startIcon={<AttachFileRounded />}>
        Browse
        <input hidden accept="image/*" name="file" type="file" onChange={handleInputOnChange} />
      </Button>
    </Box>
  );
};

interface DocumentCardProps {
  employee: Employee;
  type: string,
  file: string,
  expire: string,
};
const DocumentCard = (props: DocumentCardProps) => {
  const { employee, type, file, expire } = props;

  const expiration = DateTime.fromFormat(expire, 'yyyyMMdd');
  const nextMonths = DateTime.utc().plus({ month: 3 });
  const willExpireSoon = expiration <= nextMonths;

  const [showCreateRequest, setShowCreateRequest] = useState(false);

  const handleCreateRequestOnClick = () => setShowCreateRequest(true);

  const handlePassportOnRequest = () => {

  };

  return (
    <>
      <Card sx={{ width: 345, borderWidth: 1, }}>
        <CardMedia
          component="img"
          height="200"
          image={file}
          alt={type}
        />
        <CardContent>
          <Typography gutterBottom variant="h5" component="div">
            {type}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Image of {type} document for employee.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {expiration.toFormat('MMMM dd, yyyy')}
          </Typography>
        </CardContent>
        {(type === 'Passport' && willExpireSoon) && (
          <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.passport']}>
            <CardActions>
              <Button size="small" color="primary" onClick={handleCreateRequestOnClick}>
                Create Request
              </Button>
              <Button size="small" color="primary">
                Link Request
              </Button>
            </CardActions>
          </SecureComponent>
        )}
      </Card>
      {(type === 'Passport' && willExpireSoon) && (
        <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.passport']}>
          <Dialog open={showCreateRequest} onClose={() => setShowCreateRequest(false)} maxWidth='sm' fullWidth>
            <DialogTitle>Create Passport Renewal Request</DialogTitle>
            <Divider />
            <PassportRequestForm employee={employee} fixedType="Renewal" onClosed={() => setShowCreateRequest(false)}  />
          </Dialog>
        </SecureComponent>
      )}
    </>
  );
};

export const DocumentsBox = () => {
  const db = getFirestore();
  const storage = getStorage();

  const { state } = useLocation();
  const { idNumber } = useParams();
  const { employee }: EmployeePageContext = useOutletContext();
  const [personalDocuments, setPersonalDocuments] = useState<Array<{
    title: string,
    file: string,
    expire: string,
  }>>([]);

  // Document upload
  const [showUpload, setShowUplaod] = useState(false);
  const [documentType, setDocumentType] = useState('Iqama');
  const [documentExpire, setDocumentExpire] = useState<DateTime>(DateTime.now());
  const [documentData, setDocumentData] = useState('');
  
  const authState = useAppSelector(s => s.auth);

  // const adminOnly = authState.URB && authState.URB.roles.some((r: string) => ['super', 'admin'].includes(r));

  const handleUploadModalOnOpen = () => {
    setShowUplaod(true);
  };

  const handleUploadModalOnSubmit = () => {
    if (documentData) {
      const dateString = documentExpire.toFormat("yyyyMMdd");

      const dest = `${idNumber}/Documents/${dateString}_${documentType}`;
      uploadString(ref(storage, dest),  documentData, 'data_url')
        .then(() => {
          const url = "https://firebasestorage.googleapis.com/v0/b/nadafa-ems.appspot.com/o/" + encodeURIComponent(dest) + "?alt=media";
          return setDoc(doc(db, `Employees/${idNumber}/PersonalDocuments/${documentType}`), {
            type: documentType,
            idNumber,
            name: employee?.name,
            file: url,
            expire: dateString,
          });
        })
        .then(() => {
          setDocumentType('Iqama');
          setDocumentExpire(DateTime.now());
          setDocumentData('');
          setShowUplaod(false);
        });
    }
  }

  const handleUploadModalOnClose = () => {
    setShowUplaod(false);
  };

  useEffect(() => {
    queryEmployeeDocuments(idNumber).then((docs) => {
      setPersonalDocuments(docs);
    })
  }, [idNumber]);

  if (employee === undefined) {
    return (
      <Box>
        <Alert variant="filled" severity="info">Please wait...</Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <SecureComponent permissions={PERMISSIONS_USERS}>
        <Box width="100%" mb={2} display="flex" justifyContent="end">
          <Button variant="outlined" onClick={handleUploadModalOnOpen}>UPLOAD</Button>
        </Box>
      </SecureComponent>
      {personalDocuments.length === 0 ? (
        <Alert severity="info">
          <AlertTitle>Information</AlertTitle>
          No documents have been uploaded yet.
        </Alert>
      ) : (
        <Stack direction="row" spacing={2}>
          {personalDocuments.map((v) => {
            return (
              <DocumentCard employee={employee} key={v.title + '_' + v.expire} type={v.title} file={v.file} expire={v.expire} />
            );
          })}
        </Stack>
      )}
      <SecureComponent permissions={PERMISSIONS_USERS}>
        <Dialog open={showUpload} onClose={handleUploadModalOnClose} sx={{ backdropFilter: 'blur(5px)'  }}>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please select a document to upload. Iqama and Passport are currently the supported documents.
              Please note that only images can be uploaded.
            </DialogContentText>
            <UploadDocForm 
              documentType={documentType}
              documentExpire={documentExpire}
              onExpireChange={setDocumentExpire}
              onTypeChange={setDocumentType}
              onDataChange={setDocumentData} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUploadModalOnClose}>Cancel</Button>
            <Button onClick={handleUploadModalOnSubmit}>Upload</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
    </Box>
  )
}
